// The diamond selector: four steps of preferences, then the matching stones.
// With no diamond feed it shows the invented sample set. In live mode
// (data-selector-live) the matches are live stones from the feed, and the
// stone a visitor asks about is attached to the enquiry form on the page;
// View this stone goes to the stone's own page. The feed client and the
// enquiry code load only in live mode, so demo mode never depends on them.
import {
  DEFAULT_FILTERS,
  RANGE_LIMITS,
  SHAPES,
  sampleDiamonds,
  filterDiamonds,
  validateRanges,
  readFilters,
  writeFilters,
  describeFilters,
  money,
} from "./diamond-model.js";

const escape = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
const rangeText = (min, max, unit = "") =>
  [min, max].some((n) => n !== null && !Number.isFinite(n))
    ? "Check range"
    : min === null && max === null
      ? "Open"
      : `${min ?? "Any"}–${max ?? "any"}${unit}`;
const pair = (label, value) =>
  `<div><dt>${escape(label)}</dt><dd>${escape(value)}</dd></div>`;

class DiamondSelector extends HTMLElement {
  connectedCallback() {
    if (this.controller || this.leaving) return;
    // An old link to a stone (?stone=) goes to its stone page before anything
    // is searched for; the selector stays as it is if that can't load.
    if (this.hasAttribute("data-selector-live")) {
      const leaving = leaveForStonePage();
      if (leaving) {
        this.leaving = true;
        leaving.then((left) => {
          this.leaving = left;
          if (!left && this.isConnected) this.connectedCallback();
        });
        return;
      }
    }
    this.controller = new AbortController();
    this.filters = readFilters(location.search);
    this.step = 0;
    this.limit = 8;
    this.form = this.querySelector("[data-selector-form]");
    this.shapes = Object.fromEntries(
      SHAPES.map((shape) => [
        shape,
        this.querySelector(`input[name="shapes"][value="${shape}"]`)
          .closest("label")
          .querySelector("svg").outerHTML,
      ]),
    );
    const { signal } = this.controller;
    this.matches = [];
    this.stoneFor = null;
    // Bumped by every ask and by anything that attaches or removes a stone or
    // the brief, so an "Ask Rich" answer that arrives late attaches nothing.
    this.asking = 0;
    this.feed = this.hasAttribute("data-selector-live")
      ? new LiveSelector(this, signal)
      : null;
    this.syncForm();
    this.refresh(true, { now: true });
    this.addEventListener(
      "change",
      (event) => {
        if (event.target.matches("[data-sort]"))
          this.filters.sort = event.target.value;
        else if (this.form.contains(event.target))
          this.filters = this.readForm();
        else return;
        this.limit = 8;
        this.refresh();
      },
      { signal },
    );
    this.form.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        this.goTo(this.step + 1);
      },
      { signal },
    );
    this.addEventListener("click", (event) => this.handleClick(event), {
      signal,
    });
    this.querySelector("[data-selector-fallback]").hidden = true;
    this.querySelector("[data-selector-ui]").hidden = false;
    window.addEventListener(
      "popstate",
      () => {
        this.filters = readFilters(location.search);
        this.syncForm();
        this.limit = 8;
        this.refresh(false, { now: true });
      },
      { signal },
    );
    const enquiry = document.querySelector(".enquiry-form");
    enquiry?.addEventListener(
      "change",
      (event) => {
        if (
          event.target.matches("[data-enquiry-interest-select]") &&
          event.target.value !== "Diamond sourcing"
        ) {
          this.clearBrief();
          this.dropStone();
        }
      },
      { signal },
    );
    document
      .querySelector("[data-clear-selector-brief]")
      ?.addEventListener("click", () => this.clearBrief(), { signal });
    document
      .querySelector("[data-clear-stone]")
      ?.addEventListener("click", () => this.dropStone(), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  readForm() {
    const data = new FormData(this.form);
    const filters = { ...this.filters };
    for (const key of ["shapes", "colours", "clarities"])
      filters[key] = data.getAll(key);
    for (const key of [
      "origin",
      "cut",
      "fluorescence",
      "lab",
      "polish",
      "symmetry",
    ])
      filters[key] = data.get(key);
    for (const name of Object.keys(RANGE_LIMITS))
      for (const suffix of ["Min", "Max"]) {
        const key = `${name}${suffix}`;
        const input = this.form.elements.namedItem(key);
        // The GIA ledger's ranges (length, width, height) aren't on this form.
        if (!input) continue;
        filters[key] = input.validity.badInput
          ? NaN
          : input.value === ""
            ? null
            : Number(input.value);
      }
    return filters;
  }

  syncForm() {
    for (const input of this.form.querySelectorAll("input, select")) {
      const value = this.filters[input.name];
      if (input.type === "checkbox")
        input.checked = value.includes(input.value);
      else if (input.type === "radio") input.checked = value === input.value;
      else input.value = value ?? "";
    }
    this.querySelector("[data-sort]").value = this.filters.sort;
  }

  // Demo mode filters the samples at once; live mode asks the feed.
  refresh(updateURL = true, { now = false } = {}) {
    this.errors = validateRanges(this.filters);
    this.querySelectorAll("[data-range-error]").forEach((el) => {
      el.hidden = true;
      el.textContent = "";
    });
    this.form
      .querySelectorAll("[aria-invalid]")
      .forEach((el) => el.removeAttribute("aria-invalid"));
    for (const error of this.errors) {
      const field = this.form.elements.namedItem(error.field);
      field.setAttribute("aria-invalid", "true");
      const output = field
        .closest("[data-range]")
        .querySelector("[data-range-error]");
      output.hidden = false;
      output.textContent = error.message;
    }
    if (!this.feed) this.matches = filterDiamonds(sampleDiamonds, this.filters);
    const f = this.filters;
    this.querySelector("[data-brief]").innerHTML =
      pair("Origin", f.origin) +
      pair("Shape", f.shapes.join(", ") || "Open to any") +
      pair("Carat", rangeText(f.caratMin, f.caratMax, " ct")) +
      pair(
        "Stone budget",
        [f.priceMin, f.priceMax].some((n) => n !== null && !Number.isFinite(n))
          ? "Check range"
          : f.priceMin === null && f.priceMax === null
            ? "Open"
            : `${f.priceMin === null ? "Any" : money(f.priceMin)}–${f.priceMax === null ? "any" : money(f.priceMax)}`,
      ) +
      pair("Colour", f.colours.join(", ") || "Open") +
      pair("Clarity", f.clarities.join(", ") || "Open") +
      (f.cut ? pair("Round cut", `${f.cut} or better`) : "") +
      (f.fluorescence ? pair("Fluorescence", f.fluorescence) : "") +
      (f.lab ? pair("Laboratory", f.lab) : "") +
      (f.polish ? pair("Polish", `${f.polish} or better`) : "") +
      (f.symmetry ? pair("Symmetry", `${f.symmetry} or better`) : "") +
      ["ratio", "table", "depth"]
        .filter((key) => f[`${key}Min`] !== null || f[`${key}Max`] !== null)
        .map((key) =>
          pair(
            { ratio: "L/W ratio", table: "Table %", depth: "Depth %" }[key],
            rangeText(f[`${key}Min`], f[`${key}Max`]),
          ),
        )
        .join("");
    if (this.feed) this.feed.update({ now });
    else {
      this.querySelector("[data-match-count]").textContent = this.errors.length
        ? "Check the highlighted range to continue."
        : `${this.matches.length} sample ${this.matches.length === 1 ? "match" : "matches"} for your preferences`;
      this.querySelector("[data-results-count]").textContent =
        `${this.matches.length} sample matches`;
      this.renderResults();
    }
    // An attached enquiry brief stays in sync when preferences change. A sample
    // selection is explicitly cleared so an old stone cannot contradict new filters.
    if (
      this.attached &&
      (this.errors.length ||
        this.attachedPreferences !== describeFilters(this.filters))
    )
      this.attachBrief(null, false);
    // A live stone was chosen for the preferences of the moment; it is let go
    // when they change, as the sample selection is.
    if (
      this.stoneFor !== null &&
      (this.errors.length || this.stoneFor !== describeFilters(this.filters))
    )
      this.dropStone();
    if (!this.errors.length && updateURL) {
      try {
        history.replaceState(
          history.state,
          "",
          writeFilters(this.filters, location.href),
        );
      } catch {
        /* Filtering still works in restricted embeds. */
      }
    }
  }

  renderResults() {
    this.querySelector("[data-empty]").hidden =
      this.matches.length > 0 || this.errors.length > 0;
    this.querySelector("[data-more]").hidden =
      this.limit >= this.matches.length || this.errors.length > 0;
    this.querySelector("[data-results]").innerHTML = this.matches
      .slice(0, this.limit)
      .map(
        (d) => `
      <article class="finder-result" data-diamond-id="${d.id}">
        <div class="finder-result__top"><div class="finder-result__shape" aria-hidden="true">${this.shapes[d.shape]}</div>
          <div><p class="eyebrow">${escape(d.origin)} · Sample ${d.id}</p><h3>${d.carat.toFixed(2)} ct ${d.shape}</h3><p class="finder-result__size">${d.length.toFixed(2)} × ${d.width.toFixed(2)} × ${d.height.toFixed(2)} mm</p></div>
          <p class="finder-result__price"><span>Illustrative price</span>${escape(money(d.examplePrice))}</p>
        </div>
        <dl class="finder-result__grades">${pair("Colour", d.colour)}${pair("Clarity", d.clarity)}${pair("Cut grade", d.cut || "Not assigned")}${pair("Fluorescence", d.fluorescence)}</dl>
        <details class="finder-result__details"><summary>Explore the details</summary><dl>${pair("Polish", d.polish)}${pair("Symmetry", d.symmetry)}${pair("L/W ratio", d.ratio.toFixed(2))}${pair("Table", `${d.table}%`)}${pair("Depth", `${d.depth}%`)}${pair("Sample lab field", d.lab)}</dl><p>Invented example ${d.id}. There is no grading report or real stone behind this listing.${d.cut ? "" : " Overall cut grades are not assigned to the fancy-shape examples."}</p></details>
        <button class="finder-result__discuss" type="button" data-choose="${d.id}">Discuss this example <span aria-hidden="true">↗</span></button>
        <a class="finder-result__try" href="/pages/ring-visualiser?shape=${encodeURIComponent(d.shape)}&carat=${d.carat.toFixed(2)}">See it on a hand</a>
      </article>`,
      )
      .join("");
  }

  goTo(step) {
    this.filters = this.readForm();
    this.refresh();
    if (this.errors.length) {
      const field = this.form.elements.namedItem(this.errors[0].field);
      this.showStep(Number(field.closest("[data-panel]").dataset.panel), false);
      const details = field.closest("details");
      if (details) details.open = true;
      field.focus();
      return;
    }
    this.showStep(Math.max(0, Math.min(3, step)));
  }

  showStep(step, focus = true) {
    this.step = step;
    this.dataset.step = String(step);
    const label = this.querySelector("[data-step-label]");
    if (label) label.textContent = `Step ${step + 1} of 4`;
    this.querySelectorAll("[data-panel]").forEach((panel) => {
      panel.hidden = Number(panel.dataset.panel) !== step;
    });
    this.querySelectorAll(".finder-steps [data-go-step]").forEach((button) => {
      const index = Number(button.dataset.goStep);
      if (index === step) button.setAttribute("aria-current", "step");
      else button.removeAttribute("aria-current");
      button.toggleAttribute("data-done", index < step);
    });
    this.querySelector("[data-step-controls]").hidden = step === 3;
    this.querySelector("[data-previous]").hidden = step === 0;
    this.querySelector("[data-next]").innerHTML =
      `${["Continue to size & budget", "Continue to quality", this.feed ? "See matches" : "See sample matches"][Math.min(step, 2)]} <span aria-hidden="true">→</span>`;
    if (focus) {
      const title = this.querySelector(`[data-panel="${step}"] h2`);
      title.focus({ preventScroll: true });
      this.querySelector(".finder-steps").scrollIntoView({
        block: "start",
        behavior: "instant",
      });
    }
  }

  handleClick(event) {
    const button = event.target.closest("button");
    if (!button) return;
    if (button.hasAttribute("data-go-step"))
      this.goTo(Number(button.dataset.goStep));
    if (button.hasAttribute("data-previous")) this.goTo(this.step - 1);
    if (button.hasAttribute("data-clear-group")) {
      this.filters[button.dataset.clearGroup] = [];
      this.syncForm();
      this.refresh();
    }
    if (button.hasAttribute("data-grade-group")) {
      this.filters[button.dataset.gradeGroup] =
        button.dataset.grades.split(",");
      this.syncForm();
      this.refresh();
    }
    if (button.hasAttribute("data-carat-preset")) {
      [this.filters.caratMin, this.filters.caratMax] =
        button.dataset.caratPreset.split(",").map(Number);
      this.syncForm();
      this.refresh();
    }
    if (
      button.hasAttribute("data-reset") ||
      button.hasAttribute("data-broaden")
    ) {
      const { shapes, origin } = this.filters;
      this.filters = structuredClone(DEFAULT_FILTERS);
      if (button.hasAttribute("data-broaden"))
        Object.assign(this.filters, { shapes, origin });
      this.syncForm();
      this.limit = 8;
      this.refresh();
      this.showStep(button.hasAttribute("data-reset") ? 0 : 3);
    }
    if (button.hasAttribute("data-more")) {
      if (this.feed) this.feed.showMore();
      else {
        this.limit += 8;
        this.renderResults();
      }
    }
    if (button.hasAttribute("data-ask-stone"))
      this.feed?.askFromCard(button.dataset.askStone, button);
    if (button.hasAttribute("data-choose")) {
      const diamond = this.matches.find((d) => d.id === button.dataset.choose);
      if (diamond) this.attachBrief(diamond);
    }
    if (button.hasAttribute("data-discuss")) {
      if (this.errors.length) this.goTo(3);
      else this.attachBrief();
    }
  }

  attachBrief(diamond = null, focus = true) {
    const form = document.querySelector(".enquiry-form");
    const interest = form?.querySelector("[data-enquiry-interest-select]");
    if (!interest) {
      document.querySelector("#enquire")?.scrollIntoView();
      return;
    }
    if (this.errors.length) {
      this.clearBrief();
      return;
    }
    const briefField = form.querySelector("[data-selector-brief-field]");
    const exampleField = form.querySelector("[data-selector-example-field]");
    if (!briefField || !exampleField) return;
    // One diamond context per enquiry: the preferences replace any stone.
    this.dropStone(form);
    this.attached = true;
    this.attachedPreferences = describeFilters(this.filters);
    interest.value = "Diamond sourcing";
    briefField.disabled = false;
    briefField.value = describeFilters(this.filters);
    exampleField.disabled = !diamond;
    exampleField.value = diamond
      ? `ILLUSTRATIVE ONLY: ${diamond.id}; ${diamond.origin}; ${diamond.carat.toFixed(2)} ct ${diamond.shape}; ${diamond.colour}; ${diamond.clarity}; example price ${money(diamond.examplePrice)}. Not a real listing or quotation.`
      : "";
    const summary = form.querySelector("[data-selector-brief-summary]");
    summary.textContent = diamond
      ? `Example ${diamond.id} attached: ${diamond.carat.toFixed(2)} ct ${diamond.shape}. Sample only, not a real stone.`
      : `Your diamond preferences are attached. ${describeFilters(this.filters)}`;
    form.querySelector("[data-selector-brief-context]").hidden = false;
    if (focus) this.focusEnquiry(form);
  }

  focusEnquiry(form) {
    document.querySelector("#enquire")?.scrollIntoView({ behavior: "instant" });
    form.querySelector('[autocomplete="name"]')?.focus({ preventScroll: true });
  }

  // Live mode: attach the stone to the enquiry form in place of the brief.
  // focus: bring the form into view.
  chooseStone(stone, { focus = true } = {}) {
    const form = document.querySelector(".enquiry-form");
    const attach = this.feed?.live?.attachStone;
    if (!attach || !form?.querySelector("[data-stone-field]") || !attach(form, stone)) {
      if (focus) document.querySelector("#enquire")?.scrollIntoView();
      return;
    }
    this.clearBrief();
    this.stoneFor = describeFilters(this.filters);
    if (focus) this.focusEnquiry(form);
  }

  // Only live mode attaches stones, so only it has one to remove.
  dropStone(form = document.querySelector(".enquiry-form")) {
    this.stoneFor = null;
    this.asking++;
    const detach = this.feed?.live?.detachStone;
    if (detach && form?.querySelector("[data-stone-field]")) detach(form);
  }

  clearBrief() {
    this.attached = false;
    const form = document.querySelector(".enquiry-form");
    form
      ?.querySelectorAll(
        "[data-selector-brief-field], [data-selector-example-field]",
      )
      .forEach((field) => {
        field.disabled = true;
        field.value = "";
      });
    const context = form?.querySelector("[data-selector-brief-context]");
    if (context) context.hidden = true;
  }
}

// Live mode: the matches come from the diamond feed. A change waits a moment,
// cancels the request before it and shows only the answer for the latest
// preferences. A failure says so and never falls back to sample stones.
const WAIT = 250;
const STEP = 8;
const SOLD = "This stone has just been reserved or sold.";
const SLOW = 4000; // after this, the loading state says it is still at work
const RETRIES = 2; // empty pages with more to come passed over before "no stones"
// Shown if the live modules themselves can't load: the same words as
// feedFailureHTML("unavailable") in diamond-feed.js.
const LOAD_FAILURE = `<p class="feed-failure">The live stones can't be loaded right now. Tell Rich what you're after and he'll search for you. <a href="/pages/contact?interest=diamond">Talk to Rich</a> or <a href="/pages/diamond-guide">read the diamond guide</a>.</p>`;
let modules = null;
const liveModules = () =>
  (modules ??= Promise.all([
    import("./diamond-feed.js"),
    import("./stone-enquiry.js"),
  ]).then(([feed, enquiry]) => ({ ...feed, ...enquiry })));
// An old link to a stone on the selector (?stone=X) now goes to the stone
// page, which leads back here without it. null: no such link; otherwise a
// promise of whether the page is leaving.
const leaveForStonePage = () => {
  const params = new URLSearchParams(location.search);
  const id = params.get("stone");
  if (!id || params.has("view")) return null;
  params.delete("stone");
  const query = params.toString();
  const from = location.pathname + (query ? `?${query}` : "");
  return import("./diamond-feed.js").then(
    ({ stonePageURL }) => {
      location.replace(stonePageURL(id, { from }));
      return true;
    },
    () => false,
  );
};
// Placeholder cards, shaped like the real ones, while the first answer comes.
const bar = (kind) => `<span class="skeleton skeleton-bar skeleton-bar--${kind}"></span>`;
const SKELETON = `${`<div class="finder-result finder-result--skeleton"><div class="finder-result__top"><span class="skeleton finder-result__skeleton-photo"></span><div>${bar("eyebrow")}${bar("title")}${bar("short")}</div></div>${bar("text")}<span class="skeleton skeleton-button"></span></div>`.repeat(4)}<p class="loading-label">Loading…</p>`;
const fixed = (value) => (Number.isFinite(value) ? value.toFixed(2) : "—");
const percent = (value) => (Number.isFinite(value) ? `${value}%` : "—");
// The count is a nicety: if it fails the stones still show, unless the
// visitor's own abort stopped it.
const optional = (counting) =>
  counting.catch((error) => {
    if (error?.name === "AbortError") throw error;
    return null;
  });
// The search a set of preferences asks for (sort included).
const searchKey = (filters) =>
  writeFilters(filters, "https://feed.invalid/").search;

class LiveSelector {
  constructor(selector, signal) {
    this.selector = selector;
    this.signal = signal;
    this.live = null; // the feed client and the enquiry code, once loaded
    this.base = "";
    this.stones = [];
    this.page = 1;
    this.more = false;
    this.count = 0; // the feed's total, or null if it couldn't be had
    this.approximate = false; // a budget is set, so the count is approximate
    this.shown = 0;
    this.key = null;
    this.failed = false;
    this.results = selector.querySelector("[data-results]");
    this.error = selector.querySelector("[data-results-error]");
    this.empty = selector.querySelector("[data-empty]");
    this.button = selector.querySelector("[data-more]");
    this.matchCount = selector.querySelector("[data-match-count]");
    this.resultsCount = selector.querySelector("[data-results-count]");
    this.skeleton = selector.querySelector("[data-results-skeleton]");
    this.slow = selector.querySelector("[data-results-slow]");
    this.progress = selector.querySelector("[data-results-bar]");
    this.moreLabel = this.button.textContent;
    this.ready = liveModules().then(
      (live) => {
        this.live = live;
        this.base = live.feedBase(document);
      },
      () => {},
    );
    signal.addEventListener("abort", () => this.cancel());
  }

  // A new search is on its way. Cards already shown stay, dimmed, under a
  // thin moving bar; with none shown, placeholder cards stand in. After a
  // while with no answer, a line says it is still at work.
  busy() {
    this.results.setAttribute("aria-busy", "true");
    if (this.shown) this.progress.hidden = false;
    else if (this.skeleton.hidden) {
      this.skeleton.innerHTML = SKELETON;
      this.skeleton.hidden = false;
      this.empty.hidden = true;
      this.error.hidden = true;
    }
    this.slowTimer ??= setTimeout(() => {
      this.slow.hidden = false;
    }, SLOW);
  }

  // The search has answered, failed or been set aside.
  idle() {
    this.results.removeAttribute("aria-busy");
    this.progress.hidden = true;
    this.skeleton.hidden = true;
    this.skeleton.innerHTML = "";
    clearTimeout(this.slowTimer);
    this.slowTimer = null;
    this.slow.hidden = true;
  }

  // Show more while its page loads: a spinner, and it can't be pressed twice.
  moreBusy(on) {
    if (on) {
      this.button.setAttribute("aria-disabled", "true");
      this.button.innerHTML = `<span class="spinner" aria-hidden="true"></span>Loading more stones…`;
    } else if (this.button.hasAttribute("aria-disabled")) {
      this.button.removeAttribute("aria-disabled");
      this.button.textContent = this.moreLabel;
    }
  }

  price(stone) {
    try {
      return this.live.formatPrice(stone);
    } catch {
      return "—";
    }
  }

  // error null: the live modules didn't load.
  failure(error) {
    const live = this.live;
    if (!live) return LOAD_FAILURE;
    return live.feedFailureHTML(
      error instanceof live.FeedError && error.kind === "busy" ? "busy" : "unavailable",
    );
  }

  cancel() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.request = null;
    this.moreBusy(false);
  }

  // Called on every refresh: a new search only when the preferences or the
  // sort changed, or the last one failed.
  update({ now = false } = {}) {
    if (this.selector.errors.length) {
      this.cancel();
      this.idle();
      this.key = null;
      this.matchCount.textContent = "Check the highlighted range to continue.";
      return;
    }
    const key = searchKey(this.selector.filters);
    if (key === this.key && !this.failed) return;
    this.key = key;
    this.failed = false;
    this.cancel();
    this.button.hidden = true;
    this.busy();
    this.matchCount.textContent = "Looking for live matches…";
    this.resultsCount.textContent = "Looking for live matches…";
    if (now) this.load();
    else this.timer = setTimeout(() => this.load(), WAIT);
  }

  async load(page = 1) {
    this.cancel();
    const request = (this.request = new AbortController());
    const filters = structuredClone(this.selector.filters);
    const options = { signal: request.signal };
    const fresh = page === 1;
    if (fresh) this.busy();
    else this.moreBusy(true);
    try {
      await this.ready;
      if (request !== this.request) return;
      const live = this.live;
      if (!live) return this.fail(null, !fresh);
      if (!this.base) throw new live.FeedError("unavailable");
      let [found, count] = await Promise.all([
        live.searchStones(this.base, filters, { ...options, page }),
        fresh ? optional(live.countStones(this.base, filters, options)) : null,
      ]);
      // A page the feed emptied with more to come is passed over, twice at
      // most. If the stones are still none, there are none to show.
      for (let tries = 0; !found.stones.length && found.more && tries < RETRIES; tries++) {
        if (request !== this.request) return;
        page += 1;
        found = await live.searchStones(this.base, filters, { ...options, page });
      }
      if (request !== this.request) return;
      if (!found.stones.length && fresh) found = { ...found, more: false };
      this.page = page;
      this.show(found, fresh ? { count, filters } : null);
    } catch (error) {
      if (request !== this.request || error?.name === "AbortError") return;
      this.fail(error, !fresh);
    } finally {
      if (request === this.request) {
        this.moreBusy(false);
        this.idle();
      }
    }
  }

  // fresh: { count, filters } for a first page, null for the next one.
  show({ stones, more }, fresh) {
    const selector = this.selector;
    if (fresh) {
      this.stones = stones;
      this.count = fresh.count;
      this.approximate = fresh.filters.priceMin !== null || fresh.filters.priceMax !== null;
      this.shown = 0;
      selector.limit = STEP;
    } else this.stones = this.stones.concat(stones);
    this.more = more;
    selector.matches = this.stones;
    this.error.hidden = true;
    this.error.innerHTML = "";
    this.render();
  }

  // Never fewer than the stones loaded. With a budget the count is "about":
  // it can include stones the search leaves out at the budget's edge. If the
  // count failed, it says how many are shown.
  counts() {
    const loaded = this.stones.length;
    if (!loaded && !this.more) {
      this.matchCount.textContent = "0 stones match your preferences";
      this.resultsCount.textContent = "0 matches";
      return;
    }
    if (this.count === null) {
      const n = `${this.shown.toLocaleString("en-GB")} ${this.shown === 1 ? "stone" : "stones"}`;
      this.matchCount.textContent = `Showing ${n} for your preferences`;
      this.resultsCount.textContent = `Showing ${n}`;
      return;
    }
    const total = Math.max(this.count, loaded);
    const n = `${this.approximate ? "About " : ""}${total.toLocaleString("en-GB")}`;
    this.matchCount.textContent = `${n} ${total === 1 ? "stone matches" : "stones match"} your preferences`;
    this.resultsCount.textContent = `${n} ${total === 1 ? "match" : "matches"}`;
  }

  // Draws the cards up to the limit, appending to those already shown.
  render() {
    const limit = Math.min(this.selector.limit, this.stones.length);
    const before = this.shown;
    const html = this.stones
      .slice(before, limit)
      .map((stone) => this.card(stone))
      .join("");
    if (before === 0) this.results.innerHTML = html;
    else this.results.insertAdjacentHTML("beforeend", html);
    this.shown = Math.max(before, limit);
    this.counts();
    this.empty.hidden = this.stones.length > 0 || this.more;
    // Keep keyboard focus in the list when Show more goes away.
    const focused = document.activeElement === this.button;
    this.button.hidden = !(this.shown < this.stones.length || this.more);
    if (focused && this.button.hidden)
      this.results.children[before]?.querySelector("button")?.focus();
  }

  showMore() {
    if (this.button.hasAttribute("aria-disabled")) return;
    // From what is on screen: a reset that kept the same search leaves the
    // cards in place but sets the limit back to 8.
    this.selector.limit = this.shown + STEP;
    this.render();
    if (this.selector.limit > this.stones.length && this.more)
      this.load(this.page + 1);
  }

  fail(error, appending) {
    this.error.innerHTML = this.failure(error);
    this.error.hidden = false;
    this.empty.hidden = true;
    // A next page that failed leaves the stones shown, and Show more to try
    // again.
    this.button.hidden = !appending;
    if (appending) return;
    this.failed = true;
    this.stones = [];
    this.selector.matches = [];
    this.shown = 0;
    this.results.innerHTML = "";
    this.matchCount.textContent = "";
    this.resultsCount.textContent = "";
  }

  // Ask Rich about this stone, from its card: a sold stone or a failed check
  // is said on the card.
  askFromCard(id, button) {
    if (button.hasAttribute("aria-disabled")) return;
    const note = button.closest("[data-stone-id]")?.querySelector("[data-result-note]");
    if (note) note.textContent = "";
    this.ask(id, {
      button,
      onProblem: (kind, error) => {
        if (note)
          note.innerHTML =
            kind === "sold" ? `<p class="finder-result__sold">${SOLD}</p>` : this.failure(error);
      },
    });
  }

  // The one way a stone is attached: check it is still available, then
  // attach it, unless a newer ask or the visitor's own change has come since.
  //   button: disabled while asking
  //   focus: bring the enquiry form into view once attached
  //   onProblem("sold" | "failed", error): a sold or delisted stone, or a failed check
  async ask(id, { button = null, focus = true, onProblem = () => {} } = {}) {
    const selector = this.selector;
    const token = ++selector.asking;
    const preferences = describeFilters(selector.filters);
    button?.setAttribute("aria-disabled", "true");
    try {
      await this.ready;
      const live = this.live;
      if (!live) throw new Error("The live modules did not load");
      if (!this.base) throw new live.FeedError("unavailable");
      const { stone, available } = await live.getStone(this.base, id, {
        signal: this.signal,
      });
      if (token !== selector.asking) return;
      if (!available) return onProblem("sold");
      // The preferences changed meanwhile, so this stone no longer answers them.
      if (selector.errors.length || describeFilters(selector.filters) !== preferences)
        return;
      selector.chooseStone(stone, { focus });
    } catch (error) {
      if (error?.name === "AbortError" || token !== selector.asking) return;
      // No longer listed: it has gone since the search, which is not an outage.
      const live = this.live;
      const delisted = live && error instanceof live.FeedError && error.kind === "not-found";
      onProblem(delisted ? "sold" : "failed", live ? error : null);
    } finally {
      button?.removeAttribute("aria-disabled");
    }
  }

  card(stone) {
    const { stoneTitle, treatmentLabel } = this.live;
    const shapes = this.selector.shapes;
    const outline = Object.hasOwn(shapes, stone.shape) ? shapes[stone.shape] : "";
    const id = escape(stone.id);
    const title = escape(stoneTitle(stone));
    const photo = this.live.feedURL(stone.image, "image");
    const from = location.pathname + location.search;
    const size = [stone.length, stone.width, stone.height].every(Number.isFinite)
      ? `${stone.length.toFixed(2)} × ${stone.width.toFixed(2)} × ${stone.height.toFixed(2)} mm`
      : "";
    const report = [stone.lab, stone.id].filter(Boolean).join(" ");
    const treated = treatmentLabel(stone.treatment);
    return `
      <article class="finder-result" data-stone-id="${id}">
        <div class="finder-result__top">${
          photo
            ? `<div class="finder-result__photo"><img src="${escape(photo)}" alt="Photograph of ${title}" loading="lazy" width="70" height="70"></div>`
            : `<div class="finder-result__shape" aria-hidden="true">${outline}</div>`
        }
          <div><p class="eyebrow">${escape(stone.origin || "Natural")} · ${escape(report)}</p><h3>${title}</h3>${size ? `<p class="finder-result__size">${size}</p>` : ""}${treated ? `<p class="finder-result__treatment">${escape(treated)}</p>` : ""}</div>
          <p class="finder-result__price"><span>Price</span>${escape(this.price(stone))}</p>
        </div>
        <dl class="finder-result__grades">${pair("Colour", stone.colour)}${pair("Clarity", stone.clarity)}${pair("Cut grade", stone.cut || "Not assigned")}${pair("Fluorescence", stone.fluorescence || "—")}</dl>
        <details class="finder-result__details"><summary>Explore the details</summary><dl>${pair("Polish", stone.polish || "—")}${pair("Symmetry", stone.symmetry || "—")}${pair("L/W ratio", fixed(stone.ratio))}${pair("Table", percent(stone.table))}${pair("Depth", percent(stone.depth))}${pair("Laboratory", stone.lab || "—")}${stone.treatment ? pair("Treatment", stone.treatment) : ""}</dl></details>
        <a class="finder-result__discuss" href="${escape(this.live.stonePageURL(stone.id, { from }))}" data-view-stone="${id}">View this stone<span class="visually-hidden">: ${title}</span> <span aria-hidden="true">↗</span></a>
        <button class="finder-result__discuss" type="button" data-ask-stone="${id}">Ask Rich about this stone<span class="visually-hidden">: ${title}</span> <span aria-hidden="true">↗</span></button>
        <div class="finder-result__note" data-result-note role="status"></div>
        <a class="finder-result__try" href="/pages/ring-visualiser?shape=${encodeURIComponent(stone.shape)}&amp;carat=${Number(stone.carat).toFixed(2)}">See it on a hand</a>
      </article>`;
  }
}

if (!customElements.get("diamond-selector"))
  customElements.define("diamond-selector", DiamondSelector);
